import { useId } from "react";
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from "react";
import { MicroLabel } from "./Text";

/**
 * Form controls, underlined rather than boxed.
 *
 * A bordered box around every field turns a form into a stack of rectangles; a
 * single rule under the text keeps the vertical rhythm of the grid and puts the
 * emphasis on the value the operator typed. The rule thickens and takes the
 * signal colour on focus, which is the same focus vocabulary the rest of the app
 * uses.
 *
 * `useId` wires label to control in every case. A placeholder is never used as a
 * label — it disappears exactly when the operator needs it.
 */

export const CONTROL = [
  "w-full bg-transparent px-0 py-1.5 text-body text-ink",
  "border-0 border-b border-rule rounded-none",
  "transition-colors duration-fast ease-swiss",
  "placeholder:text-ink-faint",
  "hover:border-ink-faint",
  "focus:border-signal focus:outline-none focus:ring-0",
  "disabled:text-ink-faint disabled:border-dashed",
].join(" ");

/**
 * The scaffolding every field in the panel shares: the Swiss caption above, the
 * control, and one line of hint-or-error below that is wired to it for a screen
 * reader.
 *
 * Extracted when the date pickers arrived. They are a button and a popover rather
 * than an `<input>`, so they cannot reuse `Field` itself — but a field whose label
 * is not `MicroLabel`, or whose error is not announced, is a field that looks
 * right and is not. There is one copy of that wiring and this is it.
 *
 * `render` is given the id to put on the control and the id list to describe it
 * with, because only the caller knows which element is the labelled one.
 */
export function FieldShell({
  label,
  error,
  hint,
  render,
}: {
  readonly label: string;
  readonly error?: string | undefined;
  readonly hint?: string | undefined;
  readonly render: (control: {
    readonly id: string;
    /**
     * The caption's own id. A control that is not a form element — the date
     * pickers' trigger button — is not named by a `<label for>` in every engine,
     * and where it is, the caption *replaces* the control's content instead of
     * preceding it: a screen reader hears "Dates, button" and never the date. Such
     * a control names itself with `aria-labelledby="<labelId> <id>"`, which reads
     * the caption and then its own text.
     */
    readonly labelId: string;
    readonly describedBy: string | undefined;
    readonly invalid: boolean;
  }) => ReactNode;
}) {
  const id = useId();
  const describedBy =
    error !== undefined ? `${id}-error` : hint !== undefined ? `${id}-hint` : undefined;

  return (
    <div className="flex flex-col gap-1">
      <label id={`${id}-label`} htmlFor={id}>
        <MicroLabel>{label}</MicroLabel>
      </label>
      {render({
        id,
        labelId: `${id}-label`,
        describedBy,
        invalid: error !== undefined,
      })}
      {error !== undefined ? (
        <span
          id={`${id}-error`}
          role="alert"
          className="text-micro tracking-normal normal-case text-signal"
        >
          {error}
        </span>
      ) : hint !== undefined ? (
        <span id={`${id}-hint`} className="text-micro tracking-normal normal-case text-ink-faint">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  readonly label: string;
  /** Shown under the control, in the signal colour, and announced. */
  readonly error?: string | undefined;
  readonly hint?: string | undefined;
}

export function Field({ label, error, hint, className = "", ...rest }: FieldProps) {
  return (
    <FieldShell
      label={label}
      {...(error === undefined ? {} : { error })}
      {...(hint === undefined ? {} : { hint })}
      render={({ id, describedBy, invalid }) => (
        <input
          {...rest}
          id={id}
          aria-invalid={invalid}
          {...(describedBy === undefined ? {} : { "aria-describedby": describedBy })}
          className={`${CONTROL} ${invalid ? "border-signal" : ""} ${className}`}
        />
      )}
    />
  );
}

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "id"> {
  readonly label: string;
  readonly children: ReactNode;
  /**
   * Same contract as `Field`'s: under the control, and announced with it. No
   * `error` counterpart, because a `<select>` only offers values it accepts.
   */
  readonly hint?: string | undefined;
}

/**
 * A native `<select>`. Deliberately not a custom listbox: the browser's own
 * control is keyboard-complete, screen-reader-complete, and correct on a touch
 * device, and none of the three filters in this panel need an appearance a
 * reimplementation would buy.
 */
export function SelectField({ label, children, hint, className = "", ...rest }: SelectFieldProps) {
  return (
    <FieldShell
      label={label}
      {...(hint === undefined ? {} : { hint })}
      render={({ id, describedBy }) => (
        <select
          {...rest}
          id={id}
          {...(describedBy === undefined ? {} : { "aria-describedby": describedBy })}
          className={`${CONTROL} cursor-pointer ${className}`}
        >
          {children}
        </select>
      )}
    />
  );
}

/**
 * The search box. Its own component because it carries a role: `type="search"`
 * gives it the platform clear affordance, and it is the one control in the app
 * that is wider than its label.
 */
export function SearchField(props: FieldProps) {
  return <Field {...props} type="search" autoComplete="off" spellCheck={false} />;
}
